import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import ExcelDownload from "../../../../assets/download-green.svg";
import SortDefault from "../../../../assets/sort_default.svg";
import SortAsc from "../../../../assets/sort_asc.svg";
import SortDsc from "../../../../assets/sort_desc.svg";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  Table,
  Column as RSuiteColumn,
  ColumnGroup,
  HeaderCell,
  Cell,
} from "rsuite-table";
import moment from "moment";
import { SelectPicker, Toggle, Tooltip, Whisper } from "rsuite";
import ProductInfoIcon from "../../../../assets/info-circle-grey.svg";
import { appInsights } from "../../../../router/router";
import {
  dateFormatterToFullMonthShortYear,
  formatNumberwithCommas,
  sortMonthYearDescending,
} from "../../../../utils/formatter";
type Props = {
  totalVendors: string[];
  totalDistricts: string[];
  totalDates: string[];
  skuQuoteDetailsFilters: any;
  selectedVendor: string;
  selectedDate: string;
  commodity: string;
  vendor: string;
  selectedDistrict: string;
  quotes: number;
  district: string;
  skuQuoteDetailsToggle: string;
  setSkuQuoteDetailsToggle: any;
  skuData: any[];
  vendorSummaryData: any[];
  fetchAllBenchmarkData: () => Promise<any>;
};

const getColorForDiff = (diff: number) => {
  if (diff === 0) {
    return "-";
  }

  if (diff > 5) {
    return "text-alert";
  }

  if (diff > 0 && diff <= 5) {
    return "text-warning";
  }

};

const highlightedSkuRows = new Set([
  "EMT '3/4",
  "PVC 40 '4",
  "BARE-Stranded-4/0 AWG",
  "NM-WG-10 AWG-3-C",
  "XHHW-2-Stranded-600 MCM",
  "THHN-Stranded-500 MCM",
  "XLP-USE-Stranded-6 AWG",
  "THHN-Stranded-12 AWG",
]);

const normalizeValue = (value: unknown) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();

const shouldHighlightQuoteDetailsRow = (rowData: any) => {
  const benchmarkValue = rowData?.benchmark;
  const isBenchmarkRow =
    benchmarkValue === undefined || benchmarkValue === null
      ? true
      : Number(benchmarkValue) === 1;

  if (!isBenchmarkRow) {
    return false;
  }

  const trade = String(rowData?.trade ?? "").trim();
  const size = String(rowData?.size ?? "").trim();

  const normalizedSize = normalizeValue(size);
  const normalizedCombined = normalizeValue(`${trade} ${size}`);

  const highlightedBySizeOrCombined = Array.from(highlightedSkuRows).some(
    (sku) => {
      const normalizedSku = normalizeValue(sku);
      return normalizedSku === normalizedSize || normalizedSku === normalizedCombined;
    },
  );

  return (
    highlightedBySizeOrCombined ||
    (normalizeValue(trade) === "EMT" && ["3/4", "'3/4"].includes(size)) ||
    (normalizeValue(trade) === "PVC 40" && ["4", "'4"].includes(size))
  );
};

const QuoteSummary = ({
  totalVendors,
  selectedVendor,
  selectedDistrict,
  skuQuoteDetailsFilters,
  totalDistricts,
  setSkuQuoteDetailsToggle,
  selectedDate,
  totalDates,
  commodity,
  vendor,
  quotes,
  district,
  skuQuoteDetailsToggle,
  skuData,
  vendorSummaryData,
  fetchAllBenchmarkData,
}: Props) => {
  const [tableKey, setTableKey] = useState(0); // State to force re-render
  const [sortColumn, setSortColumn] = useState();
  const [sortType, setSortType] = useState();
  const [tableData, setTableData] = useState<any>(skuData);
  const [tableDimensions, setTableDimensions] = useState({
    height: 310,
    rowHeight: 28,
  });
  useEffect(() => {
    const handleResize = () => {
      setTableKey((prevKey) => prevKey + 1); // Update key to trigger re-render
      if (window.innerWidth > 1600) {
        setTableDimensions({
          height: 450,
          rowHeight: 32,
        });
      } else if (window.innerWidth === 1600) {
        setTableDimensions({
          height: 350,
          rowHeight: 28,
        });
      } else if (window.innerWidth === 1280) {
        setTableDimensions({
          height: 310,
          rowHeight: 28,
        });
      } else if (window.innerWidth >= 1024 && window.innerWidth <= 1164) {
        setTableDimensions({
          height: 250,
          rowHeight: 20,
        });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize); // Clean up
    };
  }, []);

  const sortRows = (rows: any[], column: any, type: any) => {
    if (!column || !type) {
      return rows ?? [];
    }
    return [...(rows ?? [])].sort((a, b) => {
      let valueA = a[column];
      let valueB = b[column];
      if (typeof valueA === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }
      if (valueA > valueB) return type === "asc" ? 1 : -1;
      if (valueA < valueB) return type === "asc" ? -1 : 1;
      return 0;
    });
  };

  // Keep the local table data in sync with the latest query result whenever the
  // filters (e.g. the selected month) change. Without this, `tableData` stayed
  // frozen on the first render's data, so re-selecting a previously chosen month
  // showed the previous selection's rows instead of the new month's data.
  // Re-apply any active sort so the refreshed rows keep the user's sort order.
  useEffect(() => {
    setTableData(sortRows(skuData ?? [], sortColumn, sortType));
  }, [skuData]);

  const handleSortColumn = (newSortColumn: any) => {
    let nextSortType: any;
    if (sortColumn === newSortColumn) {
      // Cycle: asc -> desc -> reset (null)
      if (sortType === "asc") {
        nextSortType = "desc";
      } else if (sortType === "desc") {
        nextSortType = null; // Reset to original data
      } else {
        nextSortType = "asc";
      }
    } else {
      // If a new column is selected, always start with ascending order
      nextSortType = "asc";
    }
    setSortColumn(newSortColumn);
    setSortType(nextSortType);
    if (!nextSortType) {
      setTableData(skuData); // Reset if cycle reaches `null`
      return;
    }
    setTableData(sortRows(skuData, newSortColumn, nextSortType));
  };

  const hasManyColumns =
    totalDistricts.length > 5 ||
    totalVendors.length > 5 ||
    totalDates.length > 5;

  const tradeMinWidth =
    commodity === "Copper Wire"
      ? hasManyColumns
        ? 100
        : 220
      : hasManyColumns
        ? 100
        : 160;

  const sizeMinWidth =
    commodity === "Copper Wire"
      ? hasManyColumns
        ? 150
        : 260
      : hasManyColumns
        ? 100
        : 170;


  const getExcelHeaderValue = (value: string) => {
    if (skuQuoteDetailsToggle !== "month") {
      return {
        value,
        numFmt: undefined as string | undefined,
      };
    }

    const parsed = moment.utc(value, ["MMM YYYY", "MMMM YYYY"], true);

    if (!parsed.isValid()) {
      return {
        value,
        numFmt: undefined as string | undefined,
      };
    }

    return {
      value: parsed.startOf("month").toDate(),
      numFmt: "mmm yyyy",
    };
  };

  // Shared by both download handlers: fetch the "All benchmark" dataset
  // (a separate, one-off API call with benchmark forced to "All"/null,
  // issued only when the user clicks download) and derive the dynamic
  // columns (districts/suppliers/months) from that response's own
  // vendorSummary rather than the on-screen totalDistricts/totalVendors/
  // totalDates props, since those reflect only the currently-selected
  // benchmark.
  const getDownloadDataset = async () => {
    const allBenchmarkData = await fetchAllBenchmarkData();
    const rows = allBenchmarkData?.sku_quote_details_data ?? [];
    const vendorSummary = allBenchmarkData?.vendorSummary ?? {};

    const dynamicColumns =
      skuQuoteDetailsToggle === "district" || skuQuoteDetailsToggle === "supplier"
        ? Object.keys(vendorSummary).sort((a, b) => a.localeCompare(b))
        : sortMonthYearDescending(Object.keys(vendorSummary));

    return { rows, vendorSummary, dynamicColumns };
  };

  const handleExcelDownload = async () => {
    const { rows, dynamicColumns } = await getDownloadDataset();
    if (!rows.length) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Quote Summary");

    const getDynamicValue = (row: any, key: string) => {
      const lookupKey = key?.toLowerCase();
      const value = row?.[lookupKey];
      return value ?? "";
    };

    const quoteLabel =
      quotes && quotes !== 11 ? `Quote #${quotes}` : "Latest Quote";

    const metadataRows = [
      ["Commodity", commodity || "All"],
      [
        "District",
        skuQuoteDetailsFilters.districts.map((elem: any) => elem).join(", ") ||
        "All",
      ],
      [
        "Supplier",
        skuQuoteDetailsFilters.vendor.map((elem: any) => elem).join(", ") ||
        "All",
      ],
      ["Quote", quoteLabel],
      [
        "Date",
        skuQuoteDetailsFilters.date
          .map((elem: any) => dateFormatterToFullMonthShortYear(elem, true))
          .join(", ") || "All",
      ],
    ];

    metadataRows.forEach((row, index) => {
      const excelRow = worksheet.getRow(index + 2);
      excelRow.getCell(1).value = row[0];
      excelRow.getCell(2).value = row[1];
      excelRow.getCell(1).font = { bold: true };
    });

    const headerRowNumber = 8;
    const headerRow = worksheet.getRow(headerRowNumber);

    headerRow.getCell(1).value = "Trade";
    headerRow.getCell(2).value = "Size";

    dynamicColumns.forEach((columnValue, index) => {
      const cell = headerRow.getCell(index + 3);
      const header = getExcelHeaderValue(columnValue);

      cell.value = header.value;
      if (header.numFmt) {
        cell.numFmt = header.numFmt;
      }
    });

    headerRow.font = { bold: true };

    rows.forEach((item: any, index: number) => {
      const rowNumber = headerRowNumber + 1 + index;
      const row = worksheet.getRow(rowNumber);

      row.getCell(1).value = item?.trade ?? "";
      row.getCell(2).value = item?.size ?? "";

      dynamicColumns.forEach((columnKey, columnIndex) => {
        const cell = row.getCell(columnIndex + 3);
        const value = getDynamicValue(item, columnKey);

        cell.value = value;

        if (typeof value === "number") {
          cell.numFmt = "$#,##0.00";
        }
      });
    });

    worksheet.columns = [
      { width: 18 },
      { width: 14 },
      ...dynamicColumns.map(() => ({
        width: 14,
      })),
    ];

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "left",
        };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Quote Details.xlsx");
  };

  const handleExcelDownloadForWire = async () => {
    const { rows, dynamicColumns } = await getDownloadDataset();
    if (!rows.length) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Quote Summary");

    const getDynamicValue = (row: any, key: string) => {
      const lookupKey = key?.toLowerCase();
      const value = row?.[lookupKey];
      return value ?? "";
    };

    const metadataRows = [
      ["Commodity", commodity || "All"],
      [
        "District",
        skuQuoteDetailsFilters.districts.map((elem: any) => elem).join(", ") ||
        "All",
      ],
      [
        "Supplier",
        skuQuoteDetailsFilters.vendor.map((elem: any) => elem).join(", ") ||
        "All",
      ],
      [
        "Week",
        skuQuoteDetailsFilters.date
          .map((elem: any) => dateFormatterToFullMonthShortYear(elem))
          .join(",") || "All",
      ],
      [
        "Contract Type",
        skuQuoteDetailsFilters.contractTypes
          .map((elem: any) => elem)
          .join(", ") || "All",
      ],
      [
        "Contract Validity",
        skuQuoteDetailsFilters.contractValidity
          .map((elem: any) => elem)
          .join(", ") || "All",
      ],
      // ["Benchmark", skuQuoteDetailsFilters.benchmark],
       ["Benchmark", "All"],
    ];

    metadataRows.forEach((row, index) => {
      const excelRow = worksheet.getRow(index + 2);
      excelRow.getCell(1).value = row[0];
      excelRow.getCell(2).value = row[1];
      excelRow.getCell(1).font = { bold: true };
    });

    const headerRowNumber = 11;
    const headerRow = worksheet.getRow(headerRowNumber);

    headerRow.getCell(1).value = "Trade";
    headerRow.getCell(2).value = "Size";

    dynamicColumns.forEach((columnValue, index) => {
      const cell = headerRow.getCell(index + 3);
      const header = getExcelHeaderValue(columnValue);

      cell.value = header.value;
      if (header.numFmt) {
        cell.numFmt = header.numFmt;
      }
    });

    headerRow.font = { bold: true };

    rows.forEach((item: any, index: number) => {
      const rowNumber = headerRowNumber + 1 + index;
      const row = worksheet.getRow(rowNumber);

      row.getCell(1).value = item?.trade ?? "";
      row.getCell(2).value = item?.size ?? "";

      dynamicColumns.forEach((columnKey, columnIndex) => {
        const cell = row.getCell(columnIndex + 3);
        const value = getDynamicValue(item, columnKey);

        cell.value = value;

        if (typeof value === "number") {
          cell.numFmt = "$#,##0.00";
        }
      });
    });

    worksheet.columns = [
      { width: 18 },
      { width: 25 },
      ...dynamicColumns.map(() => ({
        width: 14,
      })),
    ];

    worksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "left",
        };
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Quote Details.xlsx");

    appInsights.trackEvent({
      name: "Vendor Quote Assessment: Quote Summary",
      properties: {
        description: `${localStorage.getItem(
          "userEmail"
        )} has downloaded "Quote Summary.xlsx" Report`,
      },
    });
  };

  return (
    <>
      <div className="w-full flex justify-between items-center shadow-module-card border border-[#bebebe] bg-white p-pd13 font-bold text-fs16 rounded-md basis-[5%]">
        <div className="text-[1.8vh] flex items-center">
          <div>Quote Details</div>{" "}
          {
            <>

              <div
                onClick={() => {
                  if (commodity === 'Copper Wire') {
                    handleExcelDownloadForWire()
                  } else {
                    handleExcelDownload();
                  }
                }}
                className="ml-[0.8vh] cursor-pointer"
              >
                {" "}
                <img className="h-[2.5vh]" src={ExcelDownload} />
              </div>
            </>
          }
        </div>
        <div className="flex gap-16">
          <div className="text-fs12">
            <SelectPicker
              data={commodity !== 'Copper Wire' ? [
                { label: "By Supplier", value: "supplier" },
                { label: "By District", value: "district" },
                { label: "By Month", value: "month" }
              ] : [
                { label: "By Supplier", value: "supplier" },
                { label: "By District", value: "district" },
                { label: "By Week", value: "month" }
              ]}

              onChange={(value: any) => {
                setSkuQuoteDetailsToggle(value);
              }}
              size={"sm"}
              className="px-2 w-[140px]"
              placement="bottomStart"
              menuStyle={{ width: 150 }}
              // defaultValue="supplier"
              value={skuQuoteDetailsToggle}
              cleanable={false}
            />
          </div>
        </div>
      </div>
      <div className="w-full flex justify-end items-center  p-[1vh 1vh 0.2vh 1vh] font-bold text-fs16 rounded-md basis-[2%]">
        <div>
          <div className="flex self-end justify-end items-center flex-nowrap gap-4">
            {/* <div className="flex gap-1 items-center">
              <span className="bg-deloitteGreen w-[2.5vh] h-[2.5vh]"></span>
              <span className="font-semibold text-[1.5vh]">
                Variation up to 1% of expected cost
              </span>
            </div> */}
            <div className="flex gap-1 items-center">
              <span className="bg-[#E9C400] w-[2.5vh] h-[2.5vh]"></span>
              <span className="font-semibold text-[1.5vh]">
                Within 5% of Lowest Quote
              </span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="bg-[#CD6318] w-[2.5vh] h-[2.5vh]"></span>
              <span className="font-semibold text-[1.5vh]">
                More than 5% over Lowest Quote
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="w-[79vw] sku-table h-[55vh]">
        {tableData && tableData?.length !== 0 ? (
          <Table
            headerHeight={50}
            data={tableData}
            fillHeight
            bordered
            rowClassName={(rowData) =>
              shouldHighlightQuoteDetailsRow(rowData) ? "highlight-row" : ""
            }
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={handleSortColumn}
            rowHeight={tableDimensions.rowHeight}
            cellBordered
            hover={false}

          >
            <RSuiteColumn
              flexGrow={hasManyColumns ? 0 : 1}
              minWidth={tradeMinWidth}
              fixed
              verticalAlign="middle"
            >
              <HeaderCell>Trade</HeaderCell>
              <Cell dataKey="trade" />
            </RSuiteColumn>

            <RSuiteColumn
              flexGrow={hasManyColumns ? 0 : 1}
              minWidth={sizeMinWidth}
              fixed
              verticalAlign="middle"
            >
              <HeaderCell>Size</HeaderCell>
              <Cell dataKey="size" />
            </RSuiteColumn>
            {skuQuoteDetailsToggle === 'district' && totalDistricts?.length > 0 &&
              totalDistricts?.map((item: any) => {
                return (
                  <>
                    <RSuiteColumn
                      // sortable
                      flexGrow={hasManyColumns ? 0 : 1}
                      width={180}
                      align="left"
                      verticalAlign="middle"
                    >
                      <HeaderCell>{item}</HeaderCell>
                      <Cell
                        dataKey={`${item?.toLowerCase()}_difference`}
                        className="flex justify-between items-center"
                      >
                        {(rowData) => (
                          <div className="flex w-full justify-between items-center">
                            <div>
                              {rowData[item?.toLowerCase()]
                                ? `$${formatNumberwithCommas(
                                  rowData[item?.toLowerCase()],
                                  true,
                                  2,
                                )}`
                                : "-"}
                            </div>
                            <div
                              className={`${getColorForDiff(
                                rowData[
                                `${item?.toLowerCase()}_difference`
                                ]
                              )}`}
                            >
                              {rowData[
                                `${item?.toLowerCase()}_difference`
                              ]
                                ? rowData[
                                `${item?.toLowerCase()}_difference`
                                ] + "%"
                                : "-"}
                            </div>
                          </div>
                        )}
                      </Cell>
                    </RSuiteColumn>
                  </>
                );
              })}

            {skuQuoteDetailsToggle === 'supplier' && totalVendors?.length > 0 &&
              totalVendors?.map((item: any) => {
                return (
                  <>
                    <RSuiteColumn
                      flexGrow={hasManyColumns ? 0 : 1}

                      // sortable
                      width={180}
                      align="left"
                      verticalAlign="middle"
                    >
                      <HeaderCell>
                        {vendorSummaryData[item?.toUpperCase()] ? (
                          <div className="flex flex-col items-start justify-start">
                            <span className="font-bold capitalize">
                              {commodity.toLowerCase() === 'copper wire' ? item : item?.toLowerCase()}
                            </span>
                            {commodity !== 'Copper Wire' && <span className="italic font-bold text-[#A3A3A3] text-[1.4vh]">
                              {vendorSummaryData[item?.toUpperCase()]
                                .quotation_no === 10
                                ? "Mid Month"
                                : `Quote #${vendorSummaryData[item?.toUpperCase()]
                                  .quotation_no
                                }`}{" "}
                              valid from{" "}
                              {moment
                                .utc(
                                  new Date(
                                    vendorSummaryData[
                                      item?.toUpperCase()
                                    ].quotation_start_date
                                  )
                                )
                                .format("ll")}
                            </span>}

                          </div>
                        ) : (
                          <div className="capitalize">
                            {commodity.toLowerCase() === 'copper wire' ? item : item?.toLowerCase()}
                          </div>
                        )}
                      </HeaderCell>
                      <Cell
                        dataKey={`${item?.toLowerCase()}_difference`}
                        className="flex justify-between items-center"
                      >
                        {(rowData) => (
                          <div className="flex w-full justify-between items-center">
                            <div>
                              {rowData[item?.toLowerCase()]
                                ? `$${formatNumberwithCommas(
                                  rowData[item?.toLowerCase()],
                                  true,
                                  2,
                                )}`
                                : "-"}
                            </div>
                            <div
                              className={`${getColorForDiff(
                                rowData[
                                `${item?.toLowerCase()}_difference`
                                ]
                              )}`}
                            >
                              {rowData[
                                `${item?.toLowerCase()}_difference`
                              ]
                                ? Number(
                                  rowData[
                                  `${item?.toLowerCase()}_difference`
                                  ]
                                ).toFixed(1) + "%"
                                : "-"}
                            </div>
                          </div>
                        )}
                      </Cell>
                    </RSuiteColumn>
                  </>
                );
              })}

            {skuQuoteDetailsToggle === 'month' && totalDates?.length > 0 &&
              totalDates?.map((item: any) => {
                return (
                  <>
                    <RSuiteColumn
                      // sortable
                      flexGrow={hasManyColumns ? 0 : 1}
                      width={180}
                      align="left"
                      verticalAlign="middle"
                    >
                      <HeaderCell>{dateFormatterToFullMonthShortYear(item, true)}</HeaderCell>
                      <Cell
                        dataKey={`${item?.toLowerCase()}_difference`}
                        className="flex justify-between items-center"
                      >
                        {(rowData) => (
                          <div className="flex w-full justify-between items-center">
                            <div>
                              {rowData[item?.toLowerCase()]
                                ? `$${formatNumberwithCommas(
                                  rowData[item?.toLowerCase()],
                                  true,
                                  2,
                                )}`
                                : "-"}
                            </div>
                            <div
                              className={`${getColorForDiff(
                                rowData[
                                `${item?.toLowerCase()}_difference`
                                ]
                              )}`}
                            >
                              {rowData[
                                `${item?.toLowerCase()}_difference`
                              ]
                                ? rowData[
                                `${item?.toLowerCase()}_difference`
                                ] + "%"
                                : "-"}
                            </div>
                          </div>
                        )}
                      </Cell>
                    </RSuiteColumn>
                  </>
                );
              })}

          </Table>
        ) : (
          <div className="text-center p-10 w-full bg-white">No data found</div>
        )}
      </div>
    </>
  );
};

export default QuoteSummary;